import { useQuery } from '@tanstack/react-query';
import type { api } from '@udm/contracts';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Clock, MapPinOff, Search, X } from 'lucide-react';
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { canAny, isSuperadmin } from '../../api/client';
import { fetchAlarmPage, fetchProjects } from '../../api/ops';
import { useSites } from '../../data/hooks';
import type { Site } from '../../data/source';
import { deviceStatic, useStaticVersion } from '../../realtime/deviceStatic';
import { deviceLive } from '../../realtime/stores';
import type { DeviceStatic, Presence } from '../../realtime/types';
import { ConnectivityTag, PageHeader, ProtocolTag } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { SerialListModal } from '../reports/SerialList';
import { DEVICE_ISSUES, filterDevices, SILENT_AFTER_MS, type DeviceIssue } from './filter';
import { MetricCell, PresenceCell, SignalCell } from './liveCells';

const COLS =
  'minmax(120px,1.2fr) 90px minmax(100px,0.9fr) minmax(150px,1.3fr) minmax(130px,1.2fr) minmax(120px,1fr) 110px 64px 90px 90px 80px';
const STATUSES: Presence[] = ['online', 'offline', 'unknown'];
const NO_ISSUES: readonly DeviceIssue[] = [];
/** Live status changes re-filter at most this often (the cells themselves update immediately). */
const STATUS_REFRESH_MS = 1000;
/** devices with an active "No GPS position" alarm are re-read this often (the alarm changes rarely) */
const GPS_ALARMS_STALE_MS = 5 * 60_000;

/**
 * Incomplete data of a device (user request 2026-11-02, superadmin only): no position, so not on the map; the
 * `gps_no_position` alarm (U22); nothing heard for SILENT_AFTER_MS (or never). From the registry and live data the
 * page already holds, plus the ids of the devices with that alarm.
 */
function issuesOf(d: DeviceStatic, gpsAlarm: ReadonlySet<string>, now: number): DeviceIssue[] {
  const out: DeviceIssue[] = [];
  if (d.lon === undefined) out.push('no_position');
  if (gpsAlarm.has(d.id)) out.push('gps_problem');
  const live = deviceLive.get(d.id);
  if (live?.presence !== 'online') {
    const seen = live?.lastSeenAt ? Date.parse(live.lastSeenAt) : NaN;
    if (Number.isNaN(seen) || now - seen > SILENT_AFTER_MS) out.push('silent');
  }
  return out;
}

/** Icons beside the name for the incomplete data of a device; the reason in the tooltip. */
function IssueMarks({ issues, id }: { issues: readonly DeviceIssue[]; id: string }) {
  const { t } = useTranslation();
  if (!issues.length) return null;
  const seen = deviceLive.get(id)?.lastSeenAt;
  return (
    <span className="issue-marks">
      {issues.includes('no_position') && (
        <span
          className="issue-mark"
          title={t(
            issues.includes('gps_problem')
              ? 'devices.issues.gpsProblemHint'
              : 'devices.issues.noPositionHint',
          )}
        >
          <MapPinOff size={14} aria-label={t('devices.issues.no_position')} />
        </span>
      )}
      {issues.includes('silent') && (
        <span
          className="issue-mark"
          title={
            seen
              ? t('devices.issues.silentHint', { at: formatDateTime(seen) })
              : t('devices.issues.neverHint')
          }
        >
          <Clock size={14} aria-label={t('devices.issues.silent')} />
        </span>
      )}
    </span>
  );
}

const Row = memo(function Row({
  d,
  site,
  project,
  issues,
  top,
  onOpen,
}: {
  d: DeviceStatic;
  site: string;
  project: string;
  issues: readonly DeviceIssue[];
  top: number;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="vrow"
      style={{ transform: `translateY(${top}px)`, gridTemplateColumns: COLS }}
      onClick={() => onOpen(d.id)}
      role="row"
    >
      <span className="ellipsis" style={{ fontWeight: 550 }}>
        {d.name}
        <IssueMarks issues={issues} id={d.id} />
      </span>
      <span>
        <ProtocolTag protocol={d.protocol} />
      </span>
      <span className="ellipsis muted">
        {d.model ??
          t(`catalog.deviceType.${d.deviceType}` as 'catalog.deviceType.other', {
            defaultValue: d.deviceType,
          })}
      </span>
      <span className="ellipsis">
        <ConnectivityTag device={d} />
      </span>
      <span className="ellipsis muted">{site}</span>
      <span className="ellipsis muted">{project}</span>
      <PresenceCell id={d.id} />
      <SignalCell id={d.id} />
      <MetricCell id={d.id} metric="electrical.power_w" />
      <MetricCell id={d.id} metric="electrical.voltage_v" />
      <MetricCell id={d.id} metric="light.dimmer_pct" />
    </div>
  );
});

/** Sites in tree order with their depth, and each site's subtree (for "site and everything below it"). */
function useSiteTree(sites: Site[]) {
  return useMemo(() => {
    const children = new Map<string | null, Site[]>();
    const ids = new Set(sites.map((s) => s.id));
    for (const s of sites) {
      const parent = s.parentId && ids.has(s.parentId) ? s.parentId : null;
      children.set(parent, [...(children.get(parent) ?? []), s]);
    }
    for (const list of children.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    const ordered: { site: Site; depth: number }[] = [];
    const walk = (parent: string | null, depth: number) => {
      for (const s of children.get(parent) ?? []) {
        ordered.push({ site: s, depth });
        walk(s.id, depth + 1);
      }
    };
    walk(null, 0);
    const subtree = (id: string) => {
      const out = new Set<string>([id]);
      const stack = [id];
      while (stack.length)
        for (const c of children.get(stack.pop()!) ?? [])
          if (!out.has(c.id)) {
            out.add(c.id);
            stack.push(c.id);
          }
      return out;
    };
    return { ordered, subtree, names: new Map(sites.map((s) => [s.id, s.name])) };
  }, [sites]);
}

/**
 * Device list over the scoped registry already held by the browser (the same data as the map): filters by protocol,
 * site (incl. its localities/zones), live status and name/UID are instant and need no server round trip. Only visible
 * rows exist in the DOM; live cells subscribe per device. Filters live in the URL.
 */
export function DevicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const protocol = params.get('protocol') ?? '';
  const siteId = params.get('site') ?? '';
  const status = (params.get('status') ?? '') as Presence | '';
  const connectivity = params.get('conn') ?? '';
  const project = params.get('project') ?? '';
  const issue = (params.get('issue') ?? '') as DeviceIssue | '';
  const me = useMe().data;
  const superadmin = isSuperadmin(me);
  const gpsAlarms = useQuery({
    queryKey: ['alarms', 'gps_no_position'],
    queryFn: () => fetchAlarmPage({ type: 'gps_no_position', state: 'active', limit: 500 }),
    enabled: superadmin,
    staleTime: GPS_ALARMS_STALE_MS,
  });
  const gpsAlarm = useMemo(
    () => new Set((gpsAlarms.data?.items ?? []).map((a) => a.device_id)),
    [gpsAlarms.data],
  );
  const projects = useQuery({
    queryKey: ['projects', null],
    queryFn: () => fetchProjects(),
    enabled: canAny(me, 'project.view'),
    staleTime: 60_000,
  });
  const projectNames = useMemo(
    () => new Map((projects.data?.items ?? []).map((p) => [p.id, p.name])),
    [projects.data],
  );
  const deferredQ = useDeferredValue(q);
  const set = (key: string, value: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (value) n.set(key, value);
        else n.delete(key);
        return n;
      },
      { replace: true },
    );

  const staticVersion = useStaticVersion((s) => s.version);
  const loaded = useStaticVersion((s) => s.loaded);
  const sites = useSites();
  const tree = useSiteTree(sites.data ?? []);

  // Presence changes (many while a fleet connects) re-run the filter at most once per second.
  const [statusTick, setStatusTick] = useState(0);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = deviceLive.subscribeChanges(() => {
      timer ??= setTimeout(() => {
        timer = undefined;
        setStatusTick((n) => n + 1);
      }, STATUS_REFRESH_MS);
    });
    return () => {
      off();
      clearTimeout(timer);
    };
  }, []);

  const all = useMemo(
    () => [...deviceStatic.values()].sort((a, b) => a.name.localeCompare(b.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the Map is mutable; staticVersion marks its changes
    [staticVersion],
  );
  const inSite = useMemo(() => (siteId ? tree.subtree(siteId) : null), [siteId, tree]);

  // Incomplete data per device (superadmin only); recomputed with the live presence.
  const issues = useMemo(() => {
    const m = new Map<string, DeviceIssue[]>();
    if (!superadmin) return m;
    const now = Date.now();
    for (const d of all) {
      const list = issuesOf(d, gpsAlarm, now);
      if (list.length) m.set(d.id, list);
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- statusTick: re-evaluate live presence / last seen
  }, [all, gpsAlarm, superadmin, statusTick]);

  const { items, counts } = useMemo(
    () =>
      filterDevices(
        all,
        {
          q: deferredQ,
          protocol,
          sites: inSite,
          project,
          status,
          connectivity,
          issue: superadmin ? issue : '',
        },
        (id) => deviceLive.get(id)?.presence,
        (d) => issues.get(d.id) ?? NO_ISSUES,
      ),
    // statusTick: re-evaluate live presence
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, deferredQ, inSite, project, protocol, status, connectivity, issue, issues, superadmin, statusTick],
  );

  const protocols = useMemo(
    () => [...new Set(all.map((d) => d.protocol).filter((p): p is string => Boolean(p)))].sort(),
    [all],
  );
  const active = Boolean(q || protocol || siteId || project || status || connectivity || issue);
  // Projects of the client of the chosen site (all visible ones without a site filter).
  const projectOptions = useMemo(() => {
    const items = projects.data?.items ?? [];
    return siteId ? items.filter((p) => tree.subtree(p.client_id).has(siteId)) : items;
  }, [projects.data, siteId, tree]);
  // Listed serials not installed yet (U20), for the chosen project or the projects of the chosen client.
  const notInstalled = useMemo(
    () =>
      (project ? projectOptions.filter((p) => p.id === project) : siteId ? projectOptions : []).filter(
        (p) => p.serials.not_installed > 0,
      ),
    [project, projectOptions, siteId],
  );
  const [serialsOf, setSerialsOf] = useState<api.Project | null>(null);
  const upstreams = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of all)
      if (d.upstream) m.set(d.upstream.id, [d.upstream.model, d.upstream.name].filter(Boolean).join(' '));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [all]);

  const parentRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 12,
  });

  const protocolLabel = (p: string) =>
    t(`catalog.protocol.${p}` as 'common.unknown' /* any profile key; labels live in catalog.protocol */, {
      defaultValue: p,
    });

  return (
    <div className="page" style={{ height: '100%' }}>
      <PageHeader
        title={t('devices.title')}
        subtitle={
          active
            ? t('devices.filteredCount', {
                shown: formatNumber(items.length),
                total: formatNumber(all.length),
              })
            : t('devices.subtitle', { formatted: formatNumber(all.length) })
        }
        actions={
          superadmin &&
          (counts.byIssue.get('no_position') ?? 0) > 0 &&
          issue !== 'no_position' && (
            <button
              type="button"
              className="btn btn-ghost issue-count"
              onClick={() => set('issue', 'no_position')}
            >
              <MapPinOff size={14} />{' '}
              {t('devices.issues.noPositionCount', {
                n: formatNumber(counts.byIssue.get('no_position') ?? 0),
              })}
            </button>
          )
        }
      />
      <div className="filter-bar">
        <label className="filter-search">
          <Search size={14} className="muted" />
          <input
            className="input"
            placeholder={t('devices.filters.search')}
            value={q}
            onChange={(e) => set('q', e.target.value)}
            aria-label={t('common.search')}
          />
        </label>
        <select
          className="input"
          value={protocol}
          onChange={(e) => set('protocol', e.target.value)}
          aria-label={t('devices.columns.protocol')}
        >
          <option value="">{t('devices.filters.allProtocols')}</option>
          {protocols.map((p) => (
            <option key={p} value={p}>
              {protocolLabel(p)} ({formatNumber(counts.byProtocol.get(p) ?? 0)})
            </option>
          ))}
        </select>
        <select
          className="input"
          value={siteId}
          onChange={(e) => set('site', e.target.value)}
          aria-label={t('common.site')}
        >
          <option value="">{t('common.allSites')}</option>
          {tree.ordered.map(({ site, depth }) => (
            <option key={site.id} value={site.id}>
              {' '.repeat(depth * 2)}
              {site.name}
            </option>
          ))}
        </select>
        {projectOptions.length > 0 && (
          <select
            className="input"
            value={project}
            onChange={(e) => set('project', e.target.value)}
            aria-label={t('devices.columns.project')}
          >
            <option value="">{t('devices.filters.allProjects')}</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {siteId ? p.name : `${p.client_name} · ${p.name}`}
              </option>
            ))}
          </select>
        )}
        <select
          className="input"
          value={status}
          onChange={(e) => set('status', e.target.value)}
          aria-label={t('common.status')}
        >
          <option value="">{t('devices.filters.allStatuses')}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`presence.${s}`)} ({formatNumber(counts.byStatus.get(s) ?? 0)})
            </option>
          ))}
        </select>
        {upstreams.length > 0 && (
          <select
            className="input"
            value={connectivity}
            onChange={(e) => set('conn', e.target.value)}
            aria-label={t('connectivity.title')}
          >
            <option value="">{t('connectivity.filterAll')}</option>
            <option value="direct">{t('connectivity.filterDirect')}</option>
            <option value="via">{t('connectivity.filterVia')}</option>
            {upstreams.map(([id, name]) => (
              <option key={id} value={id}>
                {t('connectivity.via', { name })}
              </option>
            ))}
          </select>
        )}
        {superadmin && (
          <select
            className="input"
            value={issue}
            onChange={(e) => set('issue', e.target.value)}
            aria-label={t('devices.issues.title')}
          >
            <option value="">{t('devices.issues.all')}</option>
            {DEVICE_ISSUES.map((i) => (
              <option key={i} value={i}>
                {t(`devices.issues.${i}`)} ({formatNumber(counts.byIssue.get(i) ?? 0)})
              </option>
            ))}
          </select>
        )}
        {active && (
          <button type="button" className="btn" onClick={() => setParams({}, { replace: true })}>
            <X size={14} /> {t('devices.filters.reset')}
          </button>
        )}
      </div>
      {notInstalled.map((p) => (
        <div key={p.id} className="notice serial-notice" role="status">
          <span>{t('serials.banner', { n: formatNumber(p.serials.not_installed), project: p.name })}</span>
          <button type="button" className="btn btn-ghost" onClick={() => setSerialsOf(p)}>
            {t('serials.view')}
          </button>
        </div>
      ))}
      {serialsOf && (
        <SerialListModal
          project={serialsOf}
          initialStatus="not_installed"
          onClose={() => setSerialsOf(null)}
        />
      )}
      <div
        className="card"
        style={{ flex: 1, minHeight: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div className="vhead" style={{ gridTemplateColumns: COLS }} role="row">
          <span>{t('common.name')}</span>
          <span>{t('devices.columns.protocol')}</span>
          <span>{t('devices.columns.model')}</span>
          <span>{t('devices.columns.connectivity')}</span>
          <span>{t('common.site')}</span>
          <span>{t('devices.columns.project')}</span>
          <span>{t('common.status')}</span>
          <span>{t('devices.columns.signal')}</span>
          <span className="num">{t('devices.columns.power')}</span>
          <span className="num">{t('devices.columns.voltage')}</span>
          <span className="num">{t('devices.columns.dimmer')}</span>
        </div>
        <div ref={parentRef} className="vlist" style={{ flex: 1 }} role="table">
          {!loaded ? (
            <div className="muted" style={{ padding: 'var(--space-4)' }}>
              {t('common.loading')}
            </div>
          ) : items.length === 0 ? (
            <div className="muted" style={{ padding: 'var(--space-4)' }}>
              {t('devices.filters.none')}
            </div>
          ) : (
            <div style={{ height: v.getTotalSize(), position: 'relative', minWidth: 1120 }}>
              {v.getVirtualItems().map((vi) => {
                const d = items[vi.index]!;
                return (
                  <Row
                    key={d.id}
                    d={d}
                    site={tree.names.get(d.siteId) ?? ''}
                    project={(d.projectId && projectNames.get(d.projectId)) || ''}
                    issues={issues.get(d.id) ?? NO_ISSUES}
                    top={vi.start}
                    onOpen={(id) => navigate(`/devices/${id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
